import axios from 'axios';
import { Event } from '../types';

// Use PHP proxy (works on shared hosting) or backend server
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

// Event type IDs
const EVENT_TYPES = {
  SOCCER: 1,
  CRICKET: 4
};

// Mock data - fallback only if API fails
const mockEvents: Event[] = [
    // LIVE CRICKET EVENTS
    {
        id: '64361545-e',
        name: 'Bangladesh vs Ireland',
        date: '2025-11-11',
        time: '03:30',
        location: 'International',
        streamUrl: '',
        category: 'Cricket',
        status: 'live'
    },
    {
        id: '64169475-e',
        name: 'Peshawar Region vs Lahore Region Whites',
        date: '2025-11-11',
        time: '04:00',
        location: 'Pakistan',
        streamUrl: '',
        category: 'Cricket',
        status: 'live'
    }
];

// Transform API response to Event object
const transformApiResponse = (match: any, eventTypeId: number): Event => {
    const categoryMap: { [key: number]: string } = {
        [EVENT_TYPES.SOCCER]: 'Football',
        [EVENT_TYPES.CRICKET]: 'Cricket'
    };

    // Extract team names - handle both string and object formats
    // Soccer: team_a and team_b are strings
    // Cricket: team_a and team_b are objects with .name property
    const teamA = typeof match.team_a === 'string' 
        ? match.team_a 
        : (match.team_a?.name || match.teams_info?.home?.name || 'Team A');
    
    const teamB = typeof match.team_b === 'string' 
        ? match.team_b 
        : (match.team_b?.name || match.teams_info?.away?.name || 'Team B');

    return {
        id: match.event_id || match.id || match.match_id,
        name: match.event_name || `${teamA} vs ${teamB}`,
        date: match.start_date ? new Date(match.start_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        time: match.start_date ? new Date(match.start_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '00:00',
        location: match.venue_name || match.country || 'TBD',
        streamUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', // Placeholder - will be updated with actual stream URL
        category: categoryMap[eventTypeId] || 'Sports',
        status: match.status === 'started' ? 'live' : match.status === 'notstarted' ? 'scheduled' : 'completed',
        haveLiveIcon: match.have_live_icon || false,
        teamA: teamA,
        teamB: teamB
    };
};

// Fetch events from backend proxy by event type
const fetchEventsByType = async (eventTypeId: number): Promise<Event[]> => {
    try {
        console.log(`[${new Date().toISOString()}] Fetching type ${eventTypeId} from backend...`);
        
        let url: string;
        
        // Use appropriate API format based on environment
        if (IS_PRODUCTION || API_BASE_URL.includes('.com') || API_BASE_URL.includes('.net')) {
            // Production: Use PHP proxy
            url = `${API_BASE_URL}/proxy.php?action=events&event_type_id=${eventTypeId}`;
        } else {
            // Development: Use Node.js backend
            url = `${API_BASE_URL}/api/events/${eventTypeId}`;
        }
        
        const response = await axios.get(url, {
            timeout: 10000
        });

        console.log(`[${new Date().toISOString()}] ✅ API Response for type ${eventTypeId} received`);
        console.log(`[${new Date().toISOString()}] Response.data.count:`, response.data.count);
        
        // Get the matches array
        const allMatches = response.data.matches || [];
        console.log(`[${new Date().toISOString()}] Total matches in response for type ${eventTypeId}: ${allMatches.length}`);
        
        // Log sample matches with their statuses for debugging
        if (allMatches.length > 0) {
            console.log(`[${new Date().toISOString()}] Sample matches for type ${eventTypeId}:`, 
                allMatches.slice(0, 5).map((m: any) => ({
                    id: m.event_id,
                    name: m.event_name,
                    status: m.status,
                    have_live_icon: m.have_live_icon
                }))
            );
        }
        
        if (allMatches.length === 0) {
            console.warn(`[${new Date().toISOString()}] ⚠️ No matches returned for type ${eventTypeId}`);
            return [];
        }
        
        // Get all matches (both started and notstarted)
        const allRelevantMatches = allMatches.filter((match: any) => {
            const status = match.status || match.event_status || '';
            const isStarted = status.toLowerCase() === 'started';
            const isNotStarted = status.toLowerCase() === 'notstarted';
            const hasLiveIcon = match.have_live_icon === true;
            
            // For Cricket (type 4): Strict filtering - only show if have_live_icon is true when started
            // For Football (type 1): Show all started and notstarted matches regardless of live icon
            if (isStarted) {
                if (eventTypeId === EVENT_TYPES.CRICKET && !hasLiveIcon) {
                    console.log(`Filtering out cricket match without live icon: ${match.event_id}`);
                    return false;
                }
                // Show all started football matches, and cricket matches with live icon
                console.log(`✅ Including started match for type ${eventTypeId}: ${match.event_id} - ${match.event_name}`);
                return true;
            } else if (isNotStarted) {
                // Show all upcoming matches
                console.log(`✅ Including upcoming match for type ${eventTypeId}: ${match.event_id} - ${match.event_name}`);
                return true;
            } else {
                // Filter out completed or other status
                console.log(`❌ Filtering out match for type ${eventTypeId}: status="${status}", id="${match.event_id}"`);
                return false;
            }
        });
        
        console.log(`[${new Date().toISOString()}] Relevant matches for type ${eventTypeId} after filter: ${allRelevantMatches.length}`);
        
        if (allRelevantMatches.length === 0) {
            console.warn(`[${new Date().toISOString()}] ⚠️ No relevant matches after filtering for type ${eventTypeId}`);
            if (allMatches.length > 0) {
                console.log(`[${new Date().toISOString()}] Sample match statuses:`, allMatches.slice(0, 3).map((m: any) => ({ id: m.event_id, status: m.status })));
            }
            return [];
        }
        
        // Transform to Event objects
        const events = allRelevantMatches.map((match: any) => {
            const event = transformApiResponse(match, eventTypeId);
            // Get upvotes from localStorage
            const storedUpvotes = localStorage.getItem(`upvotes_${event.id}`);
            event.upvotes = storedUpvotes ? parseInt(storedUpvotes) : 0;
            return event;
        });
        console.log(`[${new Date().toISOString()}] ✅ Transformed ${events.length} events for type ${eventTypeId}`);
        
        return events;
    } catch (error: any) {
        console.error(`[${new Date().toISOString()}] ❌ Error fetching events for type ${eventTypeId}:`, error.message);
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error message:', error.message);
        }
        return [];
    }
};

export const fetchSportsEvents = async (): Promise<Event[]> => {
    try {
        console.log(`[${new Date().toISOString()}] 🚀 Starting sports events fetch...`);
        
        // Fetch both Soccer (type 1) and Cricket (type 4) matches from real API
        const [soccerEvents, cricketEvents] = await Promise.all([
            fetchEventsByType(EVENT_TYPES.SOCCER),
            fetchEventsByType(EVENT_TYPES.CRICKET)
        ]);

        console.log(`[${new Date().toISOString()}] 🏟️ Soccer events fetched:`, soccerEvents.length);
        console.log(`[${new Date().toISOString()}] 🏏 Cricket events fetched:`, cricketEvents.length);

        // Filter to show only today and tomorrow's matches
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 2); // End of tomorrow
        
        const filteredEvents = [...soccerEvents, ...cricketEvents].filter(event => {
            const eventDate = new Date(event.date);
            eventDate.setHours(0, 0, 0, 0);
            return eventDate >= today && eventDate < tomorrow;
        });
        
        console.log(`[${new Date().toISOString()}] 📅 Events after date filter (today/tomorrow only):`, filteredEvents.length);

        // Sort by: 1) Live status, 2) Upvotes (descending), 3) Date
        const allEvents = filteredEvents.sort((a, b) => {
            // Live matches first
            if (a.status === 'live' && b.status !== 'live') return -1;
            if (a.status !== 'live' && b.status === 'live') return 1;
            
            // Then by upvotes (higher first)
            const upvoteDiff = (b.upvotes || 0) - (a.upvotes || 0);
            if (upvoteDiff !== 0) return upvoteDiff;
            
            // Then by date/time
            return new Date(a.date + ' ' + a.time).getTime() - new Date(b.date + ' ' + b.time).getTime();
        });

        console.log(`[${new Date().toISOString()}] 📊 Total combined events:`, allEvents.length);
        console.log(`[${new Date().toISOString()}] Events breakdown:`, {
            soccer: soccerEvents.length,
            cricket: cricketEvents.length,
            total: allEvents.length
        });

        // If we got real API events, use them. Otherwise fall back to mock data
        if (allEvents.length > 0) {
            console.log(`[${new Date().toISOString()}] ✅ Using real API events`);
            return allEvents;
        }
        
        console.warn(`[${new Date().toISOString()}] ⚠️ No real API events found, falling back to mock data`);
        return mockEvents;
    } catch (error) {
        console.error(`[${new Date().toISOString()}] ❌ Error in fetchSportsEvents:`, error);
        // Fallback to mock data
        return mockEvents;
    }
};

export const fetchEventDetails = async (eventId: string): Promise<Event | null> => {
    try {
        // Fetch all events and find the one with matching ID
        const allEvents = await fetchSportsEvents();
        return allEvents.find(e => e.id === eventId) || null;
    } catch (error) {
        console.error('Error fetching event details:', error);
        // Fallback to mock data
        return mockEvents.find(e => e.id === eventId) || null;
    }
};

// Get live stream URL by event ID (via backend proxy)
export const getLiveStreamUrl = async (eventId: string): Promise<string | null> => {
    try {
        console.log(`Attempting to fetch live stream URL for event ${eventId}...`);
        
        let url: string;
        
        // Use appropriate API format based on environment
        if (IS_PRODUCTION || API_BASE_URL.includes('.com') || API_BASE_URL.includes('.net')) {
            // Production: Use PHP proxy
            url = `${API_BASE_URL}/proxy.php?action=stream&event_id=${eventId}`;
        } else {
            // Development: Use Node.js backend
            url = `${API_BASE_URL}/api/stream/${eventId}`;
        }
        
        const response = await axios.get(url, {
            timeout: 10000
        });

        console.log(`Live stream response for event ${eventId}:`, response.data);

        // Extract stream URL from response
        let streamUrl = response.data?.data?.stream_url || 
                       response.data?.stream_url || 
                       response.data?.url ||
                       response.data?.glive_url ||
                       null;
        
        // If URL is found, ensure it's properly formatted
        if (streamUrl) {
            // Make sure it's a valid URL
            if (!streamUrl.startsWith('http')) {
                streamUrl = 'https://' + streamUrl;
            }
            console.log(`Live stream URL fetched for event ${eventId}:`, streamUrl);
            return streamUrl;
        } else {
            console.warn(`No stream URL found in response for event ${eventId}`, response.data);
            return null;
        }
    } catch (error: any) {
        console.error(`Error fetching live stream URL for event ${eventId}:`, error);
        
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        } else if (error.request) {
            console.error('No response received (CORS or network issue):', error.message);
        } else {
            console.error('Error message:', error.message);
        }
        
        return null;
    }
};

// Upvote functions
export const upvoteEvent = (eventId: string): number => {
    const currentUpvotes = getEventUpvotes(eventId);
    const newUpvotes = currentUpvotes + 1;
    localStorage.setItem(`upvotes_${eventId}`, newUpvotes.toString());
    
    // Track if user already upvoted
    const upvotedEvents = JSON.parse(localStorage.getItem('upvoted_events') || '[]');
    if (!upvotedEvents.includes(eventId)) {
        upvotedEvents.push(eventId);
        localStorage.setItem('upvoted_events', JSON.stringify(upvotedEvents));
    }
    
    return newUpvotes;
};

export const hasUserUpvoted = (eventId: string): boolean => {
    const upvotedEvents = JSON.parse(localStorage.getItem('upvoted_events') || '[]');
    return upvotedEvents.includes(eventId);
};

export const getEventUpvotes = (eventId: string): number => {
    const stored = localStorage.getItem(`upvotes_${eventId}`);
    return stored ? parseInt(stored) : 0;
};